import { deepClone } from '@/utils/objectUtils'

// Vueform exposes some element properties (available, path) as either plain
// values or refs depending on context; unwrap defensively.
function unwrap(value: any): any {
  return value && typeof value === 'object' && 'value' in value ? value.value : value
}

// An object/group element has a fixed set of schema-keyed children we can
// descend into. A list/array element's children are dynamic numeric indices, so
// it (like a scalar) is wiped wholesale by its own path instead of descended.
function isDescendableContainer(el$: any): boolean {
  return unwrap(el$?.isObjectType) === true || unwrap(el$?.isGroupType) === true
}

// Collect the concrete leaf paths declared beneath a hidden element. For an
// object/group container that means recursing into its declared children (so we
// target only the fields THIS form owns); for anything else — a list, or a
// scalar field — the element's own path is the leaf. Child availability is
// ignored here: the parent is hidden, so its whole declared subtree is gone.
//
// This is deliberately scoped to declared children: a step's form may share a
// top-level object key with other steps in the merged risk model (e.g. a hidden
// `CoverDetails` works block alongside the cover step's `CoverDetails.proposer_details`).
// Deleting the bare container key would wipe those sibling answers too, so we
// only ever emit paths this form actually declares.
function collectDeclaredLeafPaths(el$: any, out: string[]): void {
  if (!el$ || typeof el$ !== 'object') return

  const children = isDescendableContainer(el$) ? el$.children$ : null
  if (children && typeof children === 'object' && Object.keys(children).length > 0) {
    Object.values(children).forEach((child) => collectDeclaredLeafPaths(child, out))
    return
  }

  const path = unwrap(el$.path)
  if (typeof path === 'string' && path.length > 0) out.push(path)
}

// Walks a Vueform `form$` element tree and returns the dot-paths of every
// element whose conditions are NOT met (available === false) — i.e. the fields
// currently hidden because a controlling answer changed. A hidden container is
// not deleted by its bare path; instead its declared leaf fields are collected
// (see collectDeclaredLeafPaths), so sibling data other steps wrote under the
// same shared key survives.
//
// Returns an empty array if the form runtime isn't shaped as expected, so a
// caller can safely apply the result unconditionally.
export function collectUnavailablePaths(form$: any): string[] {
  const paths: string[] = []
  const roots = form$?.elements$
  if (!roots || typeof roots !== 'object') return paths

  const visit = (el$: any): void => {
    if (!el$ || typeof el$ !== 'object') return

    const available = unwrap(el$.available)

    if (available === false) {
      collectDeclaredLeafPaths(el$, paths)
      return
    }

    const children = el$.children$
    if (children && typeof children === 'object') {
      Object.values(children).forEach(visit)
    }
  }

  Object.values(roots).forEach(visit)
  return paths
}

// Returns a deep clone of `data` with the value at each dot-path removed. Paths
// that don't resolve are skipped. Used to wipe answers for hidden fields at save
// time without mutating the caller's object.
export function omitPaths(data: any, paths: string[]): any {
  if (!data || typeof data !== 'object' || !paths?.length) return data

  const clone = deepClone(data)

  for (const path of paths) {
    const segments = path.split('.')
    let node: any = clone
    let reachable = true

    for (let i = 0; i < segments.length - 1; i++) {
      node = node?.[segments[i]]
      if (!node || typeof node !== 'object') {
        reachable = false
        break
      }
    }

    if (reachable && node && typeof node === 'object') {
      delete node[segments[segments.length - 1]]
    }
  }

  return clone
}

// Convenience: wipe, from a clone of `data`, every field currently hidden in
// `form$`. Call at save/next so stale answers from a no-longer-shown branch are
// never persisted.
//
// `protectedRoots` are top-level model keys this form must never wipe because
// they are owned by a *different* step. A step can carry a hidden mirror of
// another step's namespace (e.g. the risk-questions renovation block mirrors
// `CoverDetails.*` to prefill the Cover Details page); when that mirror is
// hidden its declared leaves would otherwise be collected here and delete the
// owning step's real answers. Skipping any path under a protected root leaves
// that cleanup to the step that actually owns the namespace.
export function clearHiddenFields(form$: any, data: any, protectedRoots: string[] = []): any {
  let paths = collectUnavailablePaths(form$)

  if (protectedRoots.length > 0) {
    const protectedSet = new Set(protectedRoots)
    paths = paths.filter((path) => !protectedSet.has(path.split('.')[0]))
  }

  return omitPaths(data, paths)
}
